import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, TrendingUp, Award, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

export const StravaStatsWidget: React.FC = () => {
  const { user } = useAuth();

  const { data: stravaStats } = useQuery({
    queryKey: ['strava-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('running_performance')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_source', 'strava');
      
      if (error) throw error;
      
      const totalRuns = data.length;
      const totalDistance = data.reduce((sum, run) => sum + (run.distance_meters || 0), 0) / 1000;
      const totalKudos = data.reduce((sum, run) => sum + ((run as any).kudos_count || 0), 0);
      const avgSufferScore = data.length > 0 
        ? data.reduce((sum, run) => sum + ((run as any).suffer_score || 0), 0) / data.length 
        : 0;
      
      return {
        totalRuns,
        totalDistance,
        totalKudos,
        avgSufferScore: Math.round(avgSufferScore)
      };
    },
    enabled: !!user,
  });

  const { data: stravaConnection } = useQuery({
    queryKey: ['strava-connection', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('strava_credentials')
        .select('athlete_firstname, athlete_lastname, last_successful_sync')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user,
  });

  if (!stravaConnection) {
    return null;
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-800">
          <Activity className="h-5 w-5 text-orange-600" />
          Strava Stats
        </CardTitle>
        <CardDescription>
          Connected as {stravaConnection.athlete_firstname} {stravaConnection.athlete_lastname}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-700">
              {stravaStats?.totalRuns || 0}
            </div>
            <div className="text-sm text-orange-600">Activities</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-700">
              {stravaStats?.totalDistance.toFixed(1) || '0.0'} km
            </div>
            <div className="text-sm text-orange-600">Distance</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-700 flex items-center justify-center gap-1">
              <Award className="h-4 w-4" />
              {stravaStats?.totalKudos || 0}
            </div>
            <div className="text-sm text-orange-600">Kudos</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-700">
              {stravaStats?.avgSufferScore || 0}
            </div>
            <div className="text-sm text-orange-600">Avg Suffer</div>
          </div>
        </div>
        
        {stravaConnection.last_successful_sync && (
          <div className="mt-4 pt-4 border-t border-orange-200">
            <p className="text-xs text-orange-600">
              Last sync: {new Date(stravaConnection.last_successful_sync).toLocaleDateString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};